/**
 * Factory: picks the right MQTT source based on config.dataSource.
 * One config flip swaps mock <-> live.
 *
 * `MqttSourceMock` (+ its `@/lib/mock/fleet` fixtures) is pulled in with a
 * dynamic `import()` so it lands in its own chunk instead of the main bundle
 * — the same trick `main.tsx` uses for the MSW REST mock. `config.dataSource`
 * is a runtime localStorage choice, not a build flag, so the mock still has to
 * be *reachable* in every build; it just shouldn't be *downloaded* by live
 * users. Keep it out of any static import graph or Rollup will silently fold
 * the chunk back into `index-*.js` (see the keycloak.ts warning at build time).
 *
 * Because `import()` is async, construction is split in two:
 *   - `initMqttSource()` — awaited once during bootstrap, before render.
 *   - `getMqttSource()`  — synchronous accessor for every consumer.
 * Both source classes are side-effect-free to construct (emission only starts
 * in `connect()`), so moving construction earlier changes no behaviour.
 *
 * The singleton lives on `globalThis` (a.k.a. `window` in the browser) so
 * Vite HMR can't drop it. When a downstream file like `MqttSourceLegacy.ts`
 * is edited in dev, Vite invalidates this module and module-level bindings
 * get re-initialised — without the `globalThis` stash, the singleton
 * would be reset to `null`, the next `getMqttSource()` would create a
 * fresh instance with empty `lastTelemetry`, and every backfilled field
 * (status / ops_status / TIS-B / battery / sats / ...) would flicker
 * back to "—" until the relevant topic refilled it. Stashing on
 * `globalThis` keeps the same instance alive across module reloads.
 * `main.tsx` does not re-run on HMR, so the stash is also what keeps
 * `getMqttSource()` answerable after this module reloads.
 */
import { config } from '@/lib/config';
import { MqttSourceLegacy } from './MqttSourceLegacy';
import type { MqttSource } from './types';

declare global {
  // eslint-disable-next-line no-var
  var __flysafe_mqtt_source__: MqttSource | undefined;
}

/**
 * Create the singleton if it doesn't exist yet. Idempotent — safe to call
 * more than once (StrictMode, HMR, a second entrypoint) and always resolves
 * to the same instance.
 */
export async function initMqttSource(): Promise<MqttSource> {
  const existing = globalThis.__flysafe_mqtt_source__;
  if (existing) return existing;

  let instance: MqttSource;
  if (config.dataSource === 'mock') {
    const { MqttSourceMock } = await import('./MqttSourceMock');
    // Re-check: an awaited import is a yield point, so a concurrent caller
    // may have won the race while we were fetching the chunk.
    const won = globalThis.__flysafe_mqtt_source__;
    if (won) return won;
    instance = new MqttSourceMock();
  } else {
    instance = new MqttSourceLegacy();
  }

  globalThis.__flysafe_mqtt_source__ = instance;
  return instance;
}

/**
 * The shared MQTT source. Requires `initMqttSource()` to have been awaited —
 * `main.tsx` does that before the first render, so any consumer running in an
 * effect, event handler or render is safe.
 */
export function getMqttSource(): MqttSource {
  const instance = globalThis.__flysafe_mqtt_source__;
  if (!instance) {
    throw new Error('getMqttSource() called before initMqttSource() — await it during bootstrap');
  }
  return instance;
}
